import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { adminAPI } from "@/services/api";
import { Building2, CheckCircle2, Eye, Package, RefreshCw, Users, XCircle } from "lucide-react";
import {
  TailAdminDataTable,
  TailAdminDetailRow,
  TailAdminEmptyState,
  TailAdminMetricCard,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const statusTone = {
  active: "success",
  suspended: "danger",
  pending: "warning",
};

const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getClients(statusFilter !== "all" ? statusFilter : null);
      setClients(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleViewDetail = async (clientId) => {
    setDetailLoading(true);
    try {
      const res = await adminAPI.getClientDetail(clientId);
      setSelectedClient(res.data);
    } catch {
      toast.error("Failed to load client detail");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (clientId, newStatus) => {
    try {
      await adminAPI.updateClientStatus(clientId, newStatus);
      toast.success(`Client ${newStatus}`);
      fetchClients();
      if (selectedClient?.id === clientId) {
        setSelectedClient({ ...selectedClient, status: newStatus });
      }
    } catch {
      toast.error("Failed to update status");
    }
  };

  const summary = useMemo(() => ({
    active: clients.filter((client) => client.status === "active").length,
    pending: clients.filter((client) => client.status === "pending").length,
    products: clients.reduce((sum, client) => sum + Number(client.product_count || 0), 0),
    enrollments: clients.reduce((sum, client) => sum + Number(client.enrollment_count || 0), 0),
  }), [clients]);

  const rows = clients.map((client) => ({
    id: client.id,
    cells: [
      <div>
        <p className="font-semibold text-gray-900">{client.name || "Unnamed client"}</p>
        <p className="mt-1 text-xs text-gray-500">{client.email}</p>
      </div>,
      <TailAdminStatusBadge tone={statusTone[client.status] || "neutral"}>{client.status || "unknown"}</TailAdminStatusBadge>,
      <span className="font-medium text-gray-700">{Number(client.product_count || 0).toLocaleString()}</span>,
      <span className="font-medium text-gray-700">{Number(client.enrollment_count || 0).toLocaleString()}</span>,
      <Button variant="outline" size="sm" onClick={() => handleViewDetail(client.id)} disabled={detailLoading}>
        <Eye className="h-4 w-4" />
        View
      </Button>,
    ],
  }));

  return (
    <div className="space-y-6">
      <TailAdminPageHeader
        eyebrow="Platform accounts"
        title="Clients"
        description="Review businesses, product count, enrollment activity, and account status."
        actions={
          <>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-11 w-44">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchClients} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <TailAdminMetricCard label="Total Clients" value={total.toLocaleString()} icon={Building2} tone="primary" helper="Registered businesses" loading={loading} />
        <TailAdminMetricCard label="Active" value={summary.active.toLocaleString()} icon={CheckCircle2} tone="success" helper="Approved accounts" loading={loading} />
        <TailAdminMetricCard label="Products" value={summary.products.toLocaleString()} icon={Package} tone="warning" helper="Client offers" loading={loading} />
        <TailAdminMetricCard label="Enrollments" value={summary.enrollments.toLocaleString()} icon={Users} tone="neutral" helper="Total proof records" loading={loading} />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.35fr_0.65fr]">
        <TailAdminPanel title="Client directory" description={`${clients.length} clients loaded for current filter.`}>
          {loading || rows.length > 0 ? (
            <TailAdminDataTable
              loading={loading}
              columns={["Client", "Status", "Products", "Enrollments", "Actions"]}
              rows={rows}
            />
          ) : (
            <TailAdminEmptyState icon={Building2} title="No clients found" description="No clients match the selected status filter." />
          )}
        </TailAdminPanel>

        <TailAdminPanel title="Client detail" description="Select a client to inspect account health.">
          {selectedClient ? (
            <div className="space-y-3">
              <TailAdminDetailRow label="Name" value={selectedClient.name || "Unnamed"} helper={selectedClient.email} />
              <TailAdminDetailRow
                label="Status"
                value={<TailAdminStatusBadge tone={statusTone[selectedClient.status] || "neutral"}>{selectedClient.status}</TailAdminStatusBadge>}
              />
              <TailAdminDetailRow label="Enrollments" value={Number(selectedClient.enrollment_count || 0).toLocaleString()} helper="Total records" />
              <TailAdminDetailRow label="Active enrollments" value={Number(selectedClient.active_enrollment_count || 0).toLocaleString()} helper="Verified records" />

              <div className="rounded-xl border border-gray-100 p-3">
                <p className="mb-2 text-sm font-medium text-gray-700">Products</p>
                {selectedClient.products?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedClient.products.map((product) => (
                      <div key={product.id} className="flex items-center gap-2 rounded-lg bg-gray-50 p-2">
                        <Package className="h-4 w-4 text-brand-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-800">{product.name}</p>
                          <p className="text-xs text-gray-500">{product.subscription_type || "Product"}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No products</p>
                )}
              </div>

              <div className="flex gap-2 border-t border-gray-100 pt-3">
                {selectedClient.status !== "active" ? (
                  <Button size="sm" className="flex-1" onClick={() => handleStatusChange(selectedClient.id, "active")}>
                    <CheckCircle2 className="h-4 w-4" />
                    Activate
                  </Button>
                ) : null}
                {selectedClient.status !== "suspended" ? (
                  <Button size="sm" variant="destructive" className="flex-1" onClick={() => handleStatusChange(selectedClient.id, "suspended")}>
                    <XCircle className="h-4 w-4" />
                    Suspend
                  </Button>
                ) : null}
              </div>
            </div>
          ) : (
            <TailAdminEmptyState icon={Eye} title="Select a client" description="Client account details and actions will appear here." />
          )}
        </TailAdminPanel>
      </div>
    </div>
  );
};

export default AdminClients;
